import * as Config from '../../config';
import Locale from '../../utils/Locale';
import * as UUID from '../../utils/Uuid';
import { AlertInfo, CardConfig, AlertType, AlertRecord, AlertCategories, DosingErrorStates } from "../Types";
import { ChemicalType } from "../../types";
import { AlertLogic } from './AlertLogic';
import { ChemicalDosingErrorConfig } from '../../config/documents/Chemistry';
import { getSanitizerTypeLocalized } from '../../config';
import { getChemicalTypeName, getAmountName } from '../../utils/Chemistry';
import { userAccessLevel } from '../../user';
import { ContentCategories } from "../../types";


export class Dosing extends AlertLogic {
    static type = AlertType.ChemicalsNotWorking;
    static category = AlertCategories.WATER_QUIALITY;
    static contentCategories = [ ContentCategories.WATER_QUALITY ];
    static title = Locale.gettext('Dosing');

    getInfo(timeZone?: string): AlertInfo {
        const alert = this.alert;
        const config = this.allConfigs;

        // Same for all alert types
        let { body, device, alertData } = this.getCommons(timeZone);
        const bowChemistry = config.Chemistry?.find(c => c.BodyOfWaterUUID === body.UUID);
        let chemicalTypeName = getChemicalTypeName(
            device.HardwareDevice?.ChemicalPumpSettings?.ChemicalType || device.ChemicalType
        ).toLowerCase();
        
        // Varying across alert types
        const ErrorMethod:ChemicalDosingErrorConfig = alertData?.ErrorMethod || {};
        let ret:CardConfig = {
            cardTitle: this.locale.gettext('Dosing Error'),
            detailTitle: this.locale.gettext('Problem dosing %1$s into %2$s', chemicalTypeName, body.Name),
            cardIllustration: 'report_frightened',
            detailIcon: 'laboratory-glassware',
            cardDescription: '',
            detailDescription: '',
            actionButtonTitle: this.locale.gettext('Try Again'),
            actionButtonVisibleFor: userAccessLevel.SharedFullAccess,
            actionButtonEnabledFor: userAccessLevel.SharedAdminAccess,
        };

        let chemType = '?';
        switch (device.HardwareDevice?.ChemicalPumpSettings?.ChemicalType || device.ChemicalType) {
            case 'MURIATIC_ACID':
            case 'CO2':
            case 'BASE':
                chemType = this.locale.gettext('pH');
                ret.cardDescription = this.locale.gettext('Failed to dose %1$s for %2$s.', chemicalTypeName, body.Name);
                break;
            case 'CHLORINE': {
                chemType = getSanitizerTypeLocalized(bowChemistry?.SanitizerTarget?.Type) ?? this.locale.gettext('chlorine level');
                ret.cardDescription = this.locale.gettext('Failed to dose %1$s for %2$s.', chemicalTypeName, body.Name);
                break;
            }
        }

        ret.detailDescription = this.locale.gettext('The %1$s of the %2$s has failed to improve',
            chemType,
            body.Name
        );
        
        switch (ErrorMethod.ErrorMethod) {
            case DosingErrorStates.FailedDoseRate:
                ret.detailDescription += ' ' + this.locale.gettext('over %1$s of the last %2$s dosing cycles.',
                    ErrorMethod.Numerator,
                    ErrorMethod.Denominator
                );
                break;
            case DosingErrorStates.Overfeed:
                ret.detailDescription += ' ' + this.locale.gettext('after dosing %1$s%2$s %3$s.',
                    ErrorMethod.OverFeedUnit === 'TIME' ? this.locale.gettext('for') + ' ' : '',
                    ErrorMethod.OverFeedAmount,
                    getAmountName(ErrorMethod.OverFeedUnit) //, ErrorMethod.OverFeedUnit === 'TIME')
                );
                break;
            default:
        }
        ret.detailDescription2 = this.locale.gettext('Please check the %1$s feed pump to ensure it is filled, connected to the plumbing correctly, powered on and is operating properly.', chemicalTypeName);

        const resetDaily = (ErrorMethod.ResumeMethod === 'DAILY' ? this.locale.gettext('until tomorrow or') : '');
        ret.detailDescription3 = this.locale.gettext('The system will not attempt to adjust the %1$s %2$s when you press "Try again"', chemType, resetDaily);

        ret.historicalDescriptionFull = [ ret.detailDescription, ret.detailDescription2 ];
       
        ret.resolveSummary = this.locale.gettext('%1$s %2$s dosing alert resolved', body.Name, chemType);
       
        
        const alertInfo = AlertInfo.fromCardConfig(ret);
        alertInfo.contentCategories = Dosing.contentCategories;
        alertInfo.created = alert.created;
        alertInfo.updated = alert.updated;
        return alertInfo;
    } 

    static mock(type:AlertType, config:Config.AllConfig):AlertRecord|AlertRecord[] {
        let res: AlertRecord[] = [];

        for (let chemType of Object.values(ChemicalType)) {
            let devices = config.Config.Devices.filter(dev => {
                return dev.DeviceType === "ChemicalPump" && 
                    (dev.ChemicalType === chemType || dev.HardwareDevice?.ChemicalPumpSettings?.ChemicalType === chemType);
            });
            if (!devices)
                continue;

            for (let device of devices) {
                res.push(new AlertRecord({
                    id: Math.floor(Math.random() * 100000),
                    uuid: UUID.modifySection(4, UUID.modifySection(3, device.UUID, "MOCK"), "AFDR" + type),
                    siteId: Math.floor(Math.random() * 100000),
                    entityUuid: device.UUID,
                    bodyUuid: config.Config.BodiesOfWater[0]?.UUID,
                    type: type,
                    data: {
                        ErrorMethod: {
                            ErrorMethod: DosingErrorStates.FailedDoseRate,
                            Numerator: 20,
                            Denominator: 30
                        }
                    }
                }));
                res.push(new AlertRecord({
                    id: Math.floor(Math.random() * 100000),
                    uuid: UUID.modifySection(4, UUID.modifySection(3, device.UUID, "MOCK"), "AOFEED" + type),
                    siteId: Math.floor(Math.random() * 100000),
                    entityUuid: device.UUID,
                    bodyUuid: config.Config.BodiesOfWater[0]?.UUID,
                    type: type,
                    data: {
                        ErrorMethod: {
                            ErrorMethod: DosingErrorStates.Overfeed,
                            OverFeedUnit: 'TIME',
                            OverFeedAmount: 60
                        }
                    }
                }));
            }
        }
        
        return res;
    }
}